import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'auth/AuthProvider';
import { PERMISSIONS } from 'auth/permissions';
import { queryKeys } from 'core/api/queryKeys';
import { firstFieldError, humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { companyPlansApi } from 'features/company-plans/companyPlans.api';
import {
  hasHistoricalPurchases,
  invalidateCompanyPlanQueries,
  listItems,
  listMeta,
} from 'features/company-plans/companyPlans.helpers';
import {
  DEFAULT_CURRENCY,
  SUPPORTED_CURRENCIES,
  formatMoney,
  mapPlanFieldErrors,
  planFormFromResource,
  planPayloadFromForm,
  validatePlanForm,
} from 'features/company-plans/money';
import { handleMutationError, StatusChip } from 'features/company-plans/CompanyPlanUi';
import { Button } from 'shared/components/Button';
import { EmptyState, ErrorState, PageHeader, Skeleton } from 'shared/components/Feedback';
import { FilterBar } from 'shared/components/FilterBar';
import { ListTable } from 'shared/components/ListTable';
import { detailsAction, RowActionsCell, RowActionsHeader } from 'shared/components/RowActionsMenu';
import { Pagination } from 'shared/components/Pagination';
import { useToast } from 'shared/components/Toast';
import { ValidationSummary, focusFirstInvalid } from 'shared/components/ValidationSummary';
import { BilingualField, SelectField, SwitchField, TextArea, TextField } from 'shared/forms/Fields';
import { useUnsavedChanges } from 'shared/utilities/useUnsavedChanges';
import { localizedName } from 'shared/utilities/format';

const PLAN_FIELD_IDS = {
  name_ar: 'name_ar',
  name_en: 'name_en',
  description_ar: 'description_ar',
  description_en: 'description_en',
  attempt_credits: 'attempt_credits',
  price: 'price',
  price_minor: 'price',
  currency: 'currency',
  validity_days: 'validity_days',
  sort_order: 'sort_order',
};

const emptyForm = planFormFromResource();

export function PlansPage() {
  const { t, locale, dir } = useI18n();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.plansManage);
  const [filters, setFilters] = useState({ page: 1, per_page: 15, is_active: '' });
  const query = useQuery({
    queryKey: queryKeys.companyPlans(filters),
    queryFn: ({ signal }) => companyPlansApi.listPlans(filters, { signal }),
  });
  const items = listItems(query.data);
  const meta = listMeta(query.data);

  if (query.isLoading) return <Skeleton rows={6} />;
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;
  }

  return (
    <div>
      <PageHeader
        title={t('companyPlans.plansTitle')}
        actions={canManage ? <Link className="btn btn-primary" to={paths.companyPlanCreate}>{t('companyPlans.planCreate')}</Link> : null}
      />
      <FilterBar
        resetLabel={t('actions.reset')}
        onReset={() => setFilters({ page: 1, per_page: 15, is_active: '' })}
      >
        <SelectField
          id="plan-status"
          label={t('companyPlans.status')}
          value={filters.is_active}
          onChange={(value) => setFilters((current) => ({ ...current, is_active: value, page: 1 }))}
          options={[
            { value: '', label: t('filters.all') },
            { value: '1', label: t('filters.active') },
            { value: '0', label: t('filters.inactive') },
          ]}
        />
      </FilterBar>
      {items.length === 0 ? <EmptyState title={t('empty.plans')} /> : (
        <ListTable caption={t('companyPlans.plansTitle')} stickyActions>
          <thead>
            <tr>
              <th>{t('companyPlans.nameEn')}</th>
              <th>{t('companyPlans.attempts')}</th>
              <th>{t('companyPlans.price')}</th>
              <th>{t('companyPlans.status')}</th>
              <th>{t('companyPlans.featured')}</th>
              <RowActionsHeader />
            </tr>
          </thead>
          <tbody>
            {items.map((plan) => (
              <tr key={plan.id}>
                <td>{localizedName(plan, locale) || plan.name_en}</td>
                <td>{plan.attempt_credits ?? '—'}</td>
                <td>{formatMoney(plan.price_minor, plan.currency, locale)}</td>
                <td><StatusChip status={plan.is_active ? 'active' : 'inactive'} t={t} /></td>
                <td>{plan.is_featured ? t('companyPlans.statuses.featured') : '—'}</td>
                <RowActionsCell
                  label={`${t('actions.rowActions')} #${plan.id}`}
                  items={[detailsAction(paths.companyPlanDetail(plan.id), t)]}
                />
              </tr>
            ))}
          </tbody>
        </ListTable>
      )}
      <Pagination
        page={meta.current_page || filters.page}
        lastPage={meta.last_page}
        total={meta.total}
        onPageChange={(page) => setFilters((current) => ({ ...current, page }))}
        label={t('pagination.page')}
        ofLabel={t('pagination.of')}
        previousLabel={t('pagination.previous')}
        nextLabel={t('pagination.next')}
        dir={dir}
      />
    </div>
  );
}

export function PlanDetailPage() {
  const { id } = useParams();
  const { t, locale } = useI18n();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.plansManage);
  const toast = useToast();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: queryKeys.companyPlan(id),
    queryFn: ({ signal }) => companyPlansApi.showPlan(id, { signal }),
  });

  const activation = useMutation({
    mutationFn: (isActive) => companyPlansApi.setPlanActivation(id, isActive),
    onSuccess: async () => {
      await invalidateCompanyPlanQueries(queryClient);
      toast.success(t('status.updated'));
    },
    onError: (error) => handleMutationError(error, { t, toast }),
  });

  if (query.isLoading) return <Skeleton rows={6} />;
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;
  }

  const plan = query.data;
  const historical = hasHistoricalPurchases(plan);

  return (
    <div>
      <PageHeader
        title={localizedName(plan, locale) || t('companyPlans.planDetail')}
        backTo={paths.companyPlans}
        actions={canManage ? (
          <>
            <Link className="btn btn-secondary" to={paths.companyPlanEdit(plan.id)}>{t('actions.edit')}</Link>
            <Button
              tone="ghost"
              loading={activation.isPending}
              onClick={() => activation.mutate(!plan.is_active)}
            >
              {plan.is_active ? t('companyPlans.deactivatePlan') : t('companyPlans.activatePlan')}
            </Button>
          </>
        ) : null}
      />
      {historical ? <p className="warning-banner" role="status">{t('companyPlans.futurePurchasesWarning')}</p> : null}
      {historical ? <p className="muted">{t('companyPlans.archiveInstead')}</p> : null}
      <dl className="definition-grid">
        <div className="definition-row"><dt>{t('companyPlans.nameAr')}</dt><dd>{plan.name_ar || '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.nameEn')}</dt><dd>{plan.name_en || '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.attempts')}</dt><dd>{plan.attempt_credits ?? '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.price')}</dt><dd>{formatMoney(plan.price_minor, plan.currency, locale)}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.currency')}</dt><dd>{plan.currency || '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.validityDays')}</dt><dd>{plan.validity_days ?? '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.status')}</dt><dd><StatusChip status={plan.is_active ? 'active' : 'inactive'} t={t} /></dd></div>
        <div className="definition-row"><dt>{t('companyPlans.featured')}</dt><dd>{plan.is_featured ? t('companyPlans.statuses.featured') : '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.sortOrder')}</dt><dd>{plan.sort_order ?? '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.purchasesCount')}</dt><dd>{plan.purchases_count ?? (historical ? t('companyPlans.purchases') : '0')}</dd></div>
      </dl>
    </div>
  );
}

export function PlanFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [dirty, setDirty] = useState(false);
  const [conflict, setConflict] = useState(null);
  useUnsavedChanges(dirty);

  const detailQuery = useQuery({
    queryKey: queryKeys.companyPlan(id),
    enabled: isEdit,
    queryFn: ({ signal }) => companyPlansApi.showPlan(id, { signal }),
  });

  useEffect(() => {
    if (!detailQuery.data) return;
    setForm(planFormFromResource(detailQuery.data));
  }, [detailQuery.data]);

  const save = useMutation({
    mutationFn: (body) => (isEdit ? companyPlansApi.updatePlan(id, body) : companyPlansApi.createPlan(body)),
    onSuccess: async (data) => {
      setDirty(false);
      await invalidateCompanyPlanQueries(queryClient);
      toast.success(t('status.saved'));
      navigate(data?.id ? paths.companyPlanDetail(data.id) : paths.companyPlans);
    },
    onError: (error) => {
      const mapped = handleMutationError(error, { t, toast, setErrors, setConflict });
      if (mapped) {
        setErrors(mapPlanFieldErrors(mapped));
        focusFirstInvalid(mapPlanFieldErrors(mapped), PLAN_FIELD_IDS);
      }
    },
  });

  const change = (key, value) => {
    setDirty(true);
    setForm((current) => ({ ...current, [key]: value }));
  };

  const historical = isEdit && hasHistoricalPurchases(detailQuery.data);

  const onSubmit = (event) => {
    event.preventDefault();
    if (save.isPending) return;
    const nextErrors = validatePlanForm(form, t);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      focusFirstInvalid(nextErrors, PLAN_FIELD_IDS);
      return;
    }
    setErrors({});
    setConflict(null);
    save.mutate(planPayloadFromForm(form));
  };

  const currencyOptions = useMemo(
    () => SUPPORTED_CURRENCIES.map((code) => ({ value: code, label: code })),
    [],
  );

  if (isEdit && detailQuery.isLoading) return <Skeleton rows={6} />;
  if (isEdit && detailQuery.isError) {
    return <ErrorState title={humanizeApiError(detailQuery.error, t)} onRetry={detailQuery.refetch} retryLabel={t('actions.retry')} />;
  }

  return (
    <form className="stack-form" onSubmit={onSubmit} noValidate>
      <PageHeader
        title={isEdit ? t('companyPlans.planEdit') : t('companyPlans.planCreate')}
        backTo={paths.companyPlans}
        actions={<Button type="submit" loading={save.isPending}>{t('actions.save')}</Button>}
      />
      {historical ? <p className="warning-banner" role="status">{t('companyPlans.futurePurchasesWarning')}</p> : null}
      {conflict ? <p className="warning-banner" role="alert">{conflict}</p> : null}
      <ValidationSummary errors={errors} title={t('forms.validationSummary')} fieldIds={PLAN_FIELD_IDS} />
      <BilingualField
        id="name"
        labelAr={t('companyPlans.nameAr')}
        labelEn={t('companyPlans.nameEn')}
        valueAr={form.name_ar}
        valueEn={form.name_en}
        onChangeAr={(value) => change('name_ar', value)}
        onChangeEn={(value) => change('name_en', value)}
        errorAr={firstFieldError(errors, 'name_ar')}
        errorEn={firstFieldError(errors, 'name_en')}
      />
      <div className="bilingual-grid">
        <TextArea
          id="description_ar"
          label={t('companyPlans.descriptionAr')}
          value={form.description_ar}
          onChange={(value) => change('description_ar', value)}
          error={firstFieldError(errors, 'description_ar')}
          dir="rtl"
          required={false}
        />
        <TextArea
          id="description_en"
          label={t('companyPlans.descriptionEn')}
          value={form.description_en}
          onChange={(value) => change('description_en', value)}
          error={firstFieldError(errors, 'description_en')}
          dir="ltr"
          required={false}
        />
      </div>
      <TextField
        id="attempt_credits"
        label={t('companyPlans.attempts')}
        type="number"
        min="1"
        step="1"
        required
        value={form.attempt_credits}
        onChange={(value) => change('attempt_credits', value)}
        error={firstFieldError(errors, 'attempt_credits')}
      />
      <TextField
        id="price"
        label={t('companyPlans.price')}
        inputMode="decimal"
        required
        value={form.price}
        onChange={(value) => change('price', value)}
        error={firstFieldError(errors, 'price')}
        hint={t('companyPlans.validation.price')}
      />
      <SelectField
        id="currency"
        label={t('companyPlans.currency')}
        value={form.currency || DEFAULT_CURRENCY}
        onChange={(value) => change('currency', value)}
        options={currencyOptions}
        error={firstFieldError(errors, 'currency')}
        required
      />
      <TextField
        id="validity_days"
        label={t('companyPlans.validityDays')}
        type="number"
        min="1"
        step="1"
        value={form.validity_days}
        onChange={(value) => change('validity_days', value)}
        error={firstFieldError(errors, 'validity_days')}
        hint={t('companyPlans.validityDaysHint')}
      />
      <TextField
        id="sort_order"
        label={t('companyPlans.sortOrder')}
        type="number"
        min="0"
        step="1"
        value={form.sort_order}
        onChange={(value) => change('sort_order', value)}
        error={firstFieldError(errors, 'sort_order')}
      />
      <SwitchField
        id="is_active"
        label={t('companyPlans.active')}
        checked={form.is_active}
        onChange={(value) => change('is_active', value)}
      />
      <SwitchField
        id="is_featured"
        label={t('companyPlans.featured')}
        checked={form.is_featured}
        onChange={(value) => change('is_featured', value)}
      />
    </form>
  );
}
